import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { themeManager } from '../utils/themeManager';
import { buildGraphData, buildHierarchicalGraphData, industryLabel } from './utils';
import { useFilters } from '../hooks/useFilters';
import { contactApi } from '../services';
import TopBar from "./TopBar";
import FiltersDialog from './FiltersDialog';
import Sidebar from './Sidebar';
import GraphView from './GraphView';
import TableView from './TableView';
import TreeView from './TreeView';
import PipelineView from './PipelineView';
import DashboardView from './DashboardView';
import ContactDetailsDialog from './ContactDetailsDialog';
import CompanyDetailPanel from './CompanyDetailPanel';
import RelationshipManagersPanel from './RelationshipManagersPanel';
import ConferencePrepPanel from './ConferencePrepPanel';
import IcebreakersPanel from './IcebreakersPanel';
import ProfilePdfImportDialog from './ProfilePdfImportDialog';
import AskSinatra from './AskSinatra';
import Icon from './Icon';
import './Graph.css';

// Companies drawn per industry in the overview; the rest are summarised by a "+N more" node
const COMPANIES_PER_INDUSTRY = 20;

// Width of the permanent left nav - also set as --nav-width (Graph.css, Sidebar.css) so the two
// stay in sync from one source
const NAV_WIDTH = 170;
const NAV_WIDTH_COLLAPSED = 52;

const NO_FILTERS = { company: '', contact: '', position: '', industry: null, advisor: null };

// API sort fields for the table's column keys
const SORT_FIELDS = {
  name: 'last_name',
  company: 'company_name',
  position: 'position_name',
  industry: 'industry',
  advisor: 'advisor_name'
};

export default function Graph() {
  const graphRef = useRef();
  const [contacts, setContacts] = useState([]);
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [theme, setTheme] = useState(() => themeManager.initializeTheme());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Bumped after edits so the current view reloads from the API
  const [dataVersion, setDataVersion] = useState(0);
  const refreshData = useCallback(() => setDataVersion(version => version + 1), []);
  const [viewMode, setViewMode] = useState('graph');
  // Top level of the overview: 'industry', or 'icp' for the ICP target lists
  const [groupBy, setGroupBy] = useState('industry');
  // Industry or ICP to open in the Tree view when a "+N more" node is clicked
  const [treeIndustry, setTreeIndustry] = useState(null);

  const [paginationData, setPaginationData] = useState({
    page: 1,
    page_size: 50,
    total_items: 0,
    total_pages: 1
  });
  const [loadAllMode, setLoadAllMode] = useState(false);
  const [userPageRequest, setUserPageRequest] = useState(1);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === '1';
    } catch (error) {
      return false;
    }
  });
  const navWidth = sidebarCollapsed ? NAV_WIDTH_COLLAPSED : NAV_WIDTH;
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('sidebarCollapsed', next ? '1' : '0');
      } catch (error) {
        console.error('Failed to save sidebar collapsed state:', error);
      }
      return next;
    });
  };

  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth - navWidth,
    height: window.innerHeight
  });

  const [hierarchicalData, setHierarchicalData] = useState(null);
  // Company whose people are shown instead of the industry overview
  const [drillDownCompany, setDrillDownCompany] = useState(null);
  const isDrillDownMode = Boolean(drillDownCompany);

  const [showCompanyPanel, setShowCompanyPanel] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showManagers, setShowManagers] = useState(false);
  const [showConferencePrep, setShowConferencePrep] = useState(false);
  const [showIcebreakers, setShowIcebreakers] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);

  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [filterDataLoaded, setFilterDataLoaded] = useState(false);

  // Pending values (not yet applied)
  const [companyNameFilter, setCompanyNameFilter] = useState('');
  const [contactNameFilter, setContactNameFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');

  // Applied values drive API calls
  const [appliedFilters, setAppliedFilters] = useState(NO_FILTERS);

  const [showFilters, setShowFilters] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [pinnedZoom, setPinnedZoom] = useState(() => {
    try {
      const saved = localStorage.getItem('preferredZoomLevel');
      return saved ? parseFloat(saved) : null;
    } catch (error) {
      console.error('Failed to load preferred zoom level:', error);
      return null;
    }
  });

  const handleThemeToggle = () => {
    const newTheme = themeManager.toggleTheme(theme);
    setTheme(newTheme);
  };

  const {
    filterContact,
    setFilterContact,
    activeApiFilters,
    setActiveApiFilters,
    activeFiltersSummary,
    resetAllFilters
  } = useFilters();

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [industriesData, advisorsData] = await Promise.all([
          contactApi.getIndustries({ limit: 100 }),
          contactApi.getSources()
        ]);
        setIndustries(industriesData);
        setAdvisors(advisorsData);
      } catch (error) {
        console.error('Failed to load filter data:', error);
      } finally {
        // Load the graph even if the filter options failed
        setFilterDataLoaded(true);
      }
    };

    loadFilterData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth - navWidth,
        height: window.innerHeight
      });
    };

    // Also recompute right away when the sidebar collapses/expands, not just on window resize
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navWidth]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      company: companyNameFilter.trim(),
      contact: contactNameFilter.trim(),
      position: positionFilter.trim(),
      industry: selectedIndustry,
      advisor: selectedAdvisor
    });
    setUserPageRequest(1);
  };

  // Ask Sinatra sets the same appliedFilters Graph/Tree/Pipeline already read - not a parallel
  // filter system. A question replaces the filter state outright (a fresh question is a fresh
  // intent, not a tweak of whatever was there); null/no overlapping fields clears it.
  const applyAskFilters = (filters) => {
    const company = (filters?.companies?.[0] || '').trim();
    const contact = (filters?.person || '').trim();
    setCompanyNameFilter(company);
    setContactNameFilter(contact);
    setAppliedFilters({ ...NO_FILTERS, company, contact });
    setUserPageRequest(1);
  };

  const hasPendingFilterChanges = useMemo(() => (
    appliedFilters.company !== companyNameFilter.trim() ||
    appliedFilters.contact !== contactNameFilter.trim() ||
    appliedFilters.position !== positionFilter.trim() ||
    (appliedFilters.industry?.name || null) !== (selectedIndustry?.name || null) ||
    (appliedFilters.advisor?.name || null) !== (selectedAdvisor?.name || null)
  ), [appliedFilters, companyNameFilter, contactNameFilter, positionFilter, selectedIndustry, selectedAdvisor]);

  // Applied filters as API parameters, shared by the overview and the drill-down
  const peopleFilterParams = useMemo(() => {
    const params = {};
    if (appliedFilters.company) params.company_filter = appliedFilters.company;
    if (appliedFilters.contact) params.contact_filter = appliedFilters.contact;
    if (appliedFilters.position) params.position_filter = appliedFilters.position;
    if (appliedFilters.industry) params.industry_filter = appliedFilters.industry.name;
    if (appliedFilters.advisor) params.advisor_filter = appliedFilters.advisor.name;
    return params;
  }, [appliedFilters]);

  // Applied filters in the shape the active-filter indicator displays
  const appliedFilterList = useMemo(() => [
    appliedFilters.company && { field: 'company_name', value: appliedFilters.company },
    appliedFilters.contact && { field: 'contact_name', value: appliedFilters.contact },
    appliedFilters.position && { field: 'position_name', value: appliedFilters.position },
    appliedFilters.industry && { field: 'industry', value: industryLabel(appliedFilters.industry.name) },
    appliedFilters.advisor && { field: 'contact_source', value: appliedFilters.advisor.name }
  ].filter(Boolean), [appliedFilters]);

  useEffect(() => {
    if (!filterDataLoaded) return undefined;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    const load = async () => {
      try {
        if (!drillDownCompany) {
          const overview = await contactApi.searchIndustryCompanyPeople({
            ...peopleFilterParams,
            group_by: groupBy,
            max_companies: COMPANIES_PER_INDUSTRY,
            limit: 100
          });
          if (cancelled) return;
          const people = (overview.data || []).reduce(
            (sum, industry) => sum + (industry.metadata?.people_count || 0), 0
          );
          setHierarchicalData(overview);
          setContacts([]);
          setPaginationData(prev => ({ ...prev, page: 1, total_items: people, total_pages: 1 }));
        } else {
          const response = await contactApi.searchContacts({
            ...peopleFilterParams,
            company_ids: [drillDownCompany.id],
            dedupe: true,
            page: loadAllMode ? 1 : userPageRequest,
            limit: loadAllMode ? 1000 : paginationData.page_size,
            sort: sortField || 'last_name',
            order: sortOrder
          });
          if (cancelled) return;
          const page = response.pagination || {};
          setContacts(response.results || []);
          setPaginationData(prev => ({
            ...prev,
            page: page.page || 1,
            total_items: page.total_items || 0,
            total_pages: page.total_pages || 1
          }));
        }
      } catch (error) {
        // A 401 means the session ended; the sign-in page takes over
        if (cancelled || error.status === 401) return;
        console.error('Failed to load contacts:', error);
        setLoadError(error.message || 'Could not load contacts');
        setContacts([]);
        setHierarchicalData(null);
        setPaginationData(prev => ({ ...prev, total_items: 0, total_pages: 1 }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    filterDataLoaded,
    dataVersion,
    peopleFilterParams,
    groupBy,
    drillDownCompany,
    userPageRequest,
    loadAllMode,
    paginationData.page_size,
    sortField,
    sortOrder
  ]);

  const graphData = useMemo(() => {
    if (isDrillDownMode) {
      return buildGraphData(contacts);
    }
    return hierarchicalData ? buildHierarchicalGraphData(hierarchicalData) : { nodes: [], links: [] };
  }, [contacts, hierarchicalData, isDrillDownMode]);

  // Track if pinned zoom has been applied to avoid re-applying on every filter change
  const [pinnedZoomApplied, setPinnedZoomApplied] = useState(false);

  useEffect(() => {
    if (graphRef?.current && pinnedZoom && graphData?.nodes?.length > 0 && !pinnedZoomApplied) {
      // Don't override a manual zoom the user already set
      const isAtDefaultZoom = Math.abs(zoomLevel - 1.0) < 0.1;

      if (isAtDefaultZoom) {
        const timer = setTimeout(() => {
          if (graphRef.current && typeof graphRef.current.zoom === 'function') {
            graphRef.current.zoom(pinnedZoom, 800);
            setZoomLevel(pinnedZoom);
            setPinnedZoomApplied(true);
          }
        }, 2500); // Wait longer for graph to settle after initial load

        return () => clearTimeout(timer);
      } else {
        setPinnedZoomApplied(true);
      }
    }
  }, [graphData?.nodes?.length, pinnedZoom, zoomLevel, pinnedZoomApplied]);

  useEffect(() => {
    setPinnedZoomApplied(false);
  }, [pinnedZoom]);

  const selectedContactDetails = useMemo(() => {
    if (!selectedNodeData || selectedNodeData.group !== 'contact') return null;
    return selectedNodeData.contactData;
  }, [selectedNodeData]);

  // The company and person panels share one dock on the right, so opening one closes the other
  useEffect(() => {
    if (selectedContactDetails) setShowCompanyPanel(false);
  }, [selectedContactDetails]);

  useEffect(() => {
    if (showCompanyPanel) setSelectedNodeData(null);
  }, [showCompanyPanel]);

  const handleRefreshContact = async (contactId) => {
    try {
      const refreshedContact = await contactApi.refreshContact(contactId);
      setSelectedNodeData(prevNode => {
        if (!prevNode || prevNode.contactData?.id !== contactId) return prevNode;
        // Update the node's data in place so the graph keeps its layout
        Object.assign(prevNode.contactData, refreshedContact);
        return { ...prevNode };
      });
    } catch (error) {
      console.error('Failed to refresh contact:', error);
    }
  };

  const handleContactCountClick = useCallback((company) => {
    setDrillDownCompany({ id: company.id, name: company.name });
    setUserPageRequest(1);
    setLoadAllMode(false);
  }, []);

  const handleBackToHierarchical = () => {
    setDrillDownCompany(null);
    setUserPageRequest(1);
  };

  const changeViewMode = (mode) => {
    setTreeIndustry(null);
    setViewMode(mode);
  };

  const changeGroupBy = (mode) => {
    setGroupBy(mode);
    setTreeIndustry(null);
    setDrillDownCompany(null);
    setUserPageRequest(1);
  };

  const handleLoadAll = () => {
    setLoadAllMode(true);
    setUserPageRequest(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= paginationData.total_pages && !loadAllMode) {
      setUserPageRequest(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    if (newPageSize === "all") {
      handleLoadAll();
    } else {
      setLoadAllMode(false);
      setUserPageRequest(1);
      setPaginationData(prev => ({
        ...prev,
        page_size: newPageSize,
        page: 1,
        total_pages: Math.ceil(prev.total_items / newPageSize)
      }));
    }
  };

  const handleNodeClick = (node) => {
    if (node.type === 'contact_count' && node.contactCountData) {
      handleContactCountClick(node.contactCountData.company);
      return;
    }

    if (node.type === 'more_companies' && node.industryName) {
      setTreeIndustry({ name: node.industryName });
      setViewMode('tree');
      return;
    }

    if (node.type === 'company' || node.group === 'company') {
      setSelectedCompany(node);
      setShowCompanyPanel(true);
      return;
    }

    setSelectedNodeData(node);
  };

  // From Ask Sinatra's results
  const handleOpenPerson = async (contactId) => {
    setSelectedNodeData({ group: 'contact', contactData: await contactApi.getContact(contactId) });
  };

  // From the Tree view's People column, which already has the full record
  const handleOpenContact = (contact) => {
    setSelectedNodeData({ group: 'contact', contactData: contact });
  };

  const handleOpenCompany = (company) => {
    handleNodeClick({ type: 'company', name: company.name, companyData: { ...company, company_name: company.name } });
  };

  const handleCloseContactDetails = () => {
    setSelectedNodeData(null);
  };

  const handleCloseCompanyPanel = () => {
    setShowCompanyPanel(false);
    setSelectedCompany(null);
  };

  const handleResetZoom = () => {
    if (graphRef?.current && typeof graphRef.current.zoomToFit === 'function') {
      graphRef.current.zoomToFit(400, 20);
    }
  };

  const handleZoomChange = (newZoomLevel) => {
    setZoomLevel(newZoomLevel);
  };

  const handlePinZoom = () => {
    if (pinnedZoom === zoomLevel) {
      setPinnedZoom(null);
      try {
        localStorage.removeItem('preferredZoomLevel');
      } catch (error) {
        console.error('Failed to remove preferred zoom level:', error);
      }
    } else {
      setPinnedZoom(zoomLevel);
      try {
        localStorage.setItem('preferredZoomLevel', zoomLevel.toString());
      } catch (error) {
        console.error('Failed to save preferred zoom level:', error);
      }
    }
  };

  const handleSortChange = (field, order) => {
    setSortField(SORT_FIELDS[field] || field);
    setSortOrder(order);
  };

  const handleResetAllFilters = () => {
    resetAllFilters();
    setSortField(null);
    setSortOrder('asc');

    setCompanyNameFilter('');
    setContactNameFilter('');
    setPositionFilter('');
    setSelectedIndustry(null);
    setSelectedAdvisor(null);
    setAppliedFilters(NO_FILTERS);
  };

  const handleIndustryChange = (industry) => {
    setSelectedIndustry(industry);
  };

  const handleAdvisorChange = (advisor) => {
    setSelectedAdvisor(advisor);
  };

  return (
    <div className="app-shell" style={{ '--nav-width': `${navWidth}px` }}>
      <Sidebar
        viewMode={viewMode}
        setViewMode={changeViewMode}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
        onOpenManagers={() => setShowManagers(true)}
        onOpenConferencePrep={() => setShowConferencePrep(true)}
        onOpenIcebreakers={() => setShowIcebreakers(true)}
        onOpenPdfImport={() => setShowPdfImport(true)}
      />
    <div className={`graph-container${loading ? ' loading-blur' : ''}${(showCompanyPanel || selectedContactDetails) ? ' graph-container--beside-panel' : ''}`}>
      <TopBar
        viewMode={viewMode}
        backAvailable={isDrillDownMode}
        backLabel={drillDownCompany?.name}
        backCount={paginationData.total_items}
        onBack={handleBackToHierarchical}
        groupBy={groupBy}
        theme={theme}
        setTheme={handleThemeToggle}
        contacts={contacts}
        loading={loading}
        paginationData={paginationData}
        loadAllMode={loadAllMode}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onResetZoom={handleResetZoom}
        activeApiFilters={appliedFilterList}
        selectedIndustry={selectedIndustry}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        zoomLevel={zoomLevel}
        pinnedZoom={pinnedZoom}
        onPinZoom={handlePinZoom}
      />

      <FiltersDialog
        isOpen={showFilters && viewMode !== 'pipeline' && viewMode !== 'dashboard'}
        onClose={() => setShowFilters(false)}
        loading={loading}
        groupBy={groupBy}
        onGroupByChange={changeGroupBy}
        companyNameFilter={companyNameFilter}
        onCompanyNameFilterChange={setCompanyNameFilter}
        contactNameFilter={contactNameFilter}
        onContactNameFilterChange={setContactNameFilter}
        positionFilter={positionFilter}
        onPositionFilterChange={setPositionFilter}
        industries={industries}
        selectedIndustry={selectedIndustry}
        onIndustryChange={handleIndustryChange}
        advisors={advisors}
        selectedAdvisor={selectedAdvisor}
        onAdvisorChange={handleAdvisorChange}
        onApplyFilters={handleApplyFilters}
        hasPendingFilterChanges={hasPendingFilterChanges}
      />

      <div className="graph-content">
        {loading && (
          <div className="loading-overlay">
            <div className={`loading-indicator ${viewMode === 'table' ? 'loading-indicator--table' : ''}`} role="status">
              Loading contacts...
            </div>
          </div>
        )}

        {loadError && !loading && (
          <div className="graph-load-error" role="alert">
            <Icon name="alert-triangle" />
            <span>{loadError}</span>
          </div>
        )}

        {viewMode === 'graph' ? (
          <GraphView
            graphRef={graphRef}
            graphData={graphData}
            windowDimensions={windowDimensions}
            theme={theme}
            selectedNodeData={selectedNodeData}
            onNodeClick={handleNodeClick}
            onZoomChange={handleZoomChange}
            pinnedZoom={pinnedZoom}
            companyInPanel={showCompanyPanel ? selectedCompany : null}
          />
        ) : viewMode === 'table' ? (
          <TableView
            contacts={contacts}
            filterContact={filterContact}
            setFilterContact={setFilterContact}
            activeApiFilters={activeApiFilters}
            setActiveApiFilters={setActiveApiFilters}
            resetAllFilters={handleResetAllFilters}
            setSelectedNodeData={setSelectedNodeData}
            theme={theme}
            onSortChange={handleSortChange}
            activeFiltersSummary={activeFiltersSummary}
          />
        ) : viewMode === 'pipeline' ? (
          <PipelineView onOpenCompany={handleOpenCompany} companyFilter={appliedFilters.company} />
        ) : viewMode === 'dashboard' ? (
          <DashboardView />
        ) : (
          <TreeView
            theme={theme}
            groupBy={groupBy}
            selectedIndustry={treeIndustry || (groupBy === 'industry' ? appliedFilters.industry : null)}
            selectedAdvisor={appliedFilters.advisor}
            companyNameFilter={appliedFilters.company}
            contactNameFilter={appliedFilters.contact}
            positionFilter={appliedFilters.position}
            loading={loading}
            onOpenCompany={handleOpenCompany}
            onOpenPerson={handleOpenContact}
          />
        )}
      </div>

      <ContactDetailsDialog
        selectedContactDetails={selectedContactDetails}
        onClose={handleCloseContactDetails}
        theme={theme}
        onRefreshContact={handleRefreshContact}
        onDataChanged={refreshData}
        onOpenCompany={handleOpenCompany}
      />

      <CompanyDetailPanel
        company={selectedCompany}
        isOpen={showCompanyPanel}
        onClose={handleCloseCompanyPanel}
        onShowPeople={(companyNode) => {
          handleCloseCompanyPanel();
          handleContactCountClick(companyNode.companyData);
        }}
        theme={theme}
        dataVersion={dataVersion}
        onDataChanged={refreshData}
      />

      <RelationshipManagersPanel
        isOpen={showManagers}
        onClose={() => setShowManagers(false)}
        onDataChanged={refreshData}
      />

      <ConferencePrepPanel
        isOpen={showConferencePrep}
        onClose={() => setShowConferencePrep(false)}
      />

      <IcebreakersPanel
        isOpen={showIcebreakers}
        onClose={() => setShowIcebreakers(false)}
      />

      <ProfilePdfImportDialog
        isOpen={showPdfImport}
        onClose={() => setShowPdfImport(false)}
        onDataChanged={refreshData}
      />

      <AskSinatra theme={theme} onOpenPerson={handleOpenPerson} onOpenCompany={handleOpenCompany} onApplyFilters={applyAskFilters} />

    </div>
    </div>
  );
}
